import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ScreenHeatmapComponent } from './screen-heatmap.component';
import { TrackingApiService } from '../services/tracking-api.service';
import { screenLabel } from '../insights-screens.util';
import type { InsightsAggregate, ScreenAggregate } from '../tracking-demo.models';

/**
 * Dashboard "Insights" tab: the cross-session behavioural aggregate for the product from
 * GET /api/tracking/insights — summed rage / dead / clicks, average scroll depth, and per
 * SCREEN (lobby, live call) a merged click heatmap over a frame of that screen plus the
 * most-clicked elements. Product-scoped via the shared TrackingApiService.
 *
 * The background frame comes from the newest recording (`sample`); clicks from every
 * session are normalized to their own viewport, so the overlay is honest at the grain of
 * "where on this screen do people click".
 */
@Component({
  selector: 'app-insights-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, ScreenHeatmapComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-wrap items-center gap-2">
      <h3 class="text-sm font-semibold text-slate-700 dark:text-ink-soft">Behavioural insights</h3>
      <select [ngModel]="days()" (ngModelChange)="onDays($event)"
        class="ml-auto cursor-pointer rounded-lg border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-3 py-1.5 text-sm text-slate-700 dark:text-ink-soft outline-none [color-scheme:light] dark:[color-scheme:dark]">
        <option [value]="7">Last 7 days</option>
        <option [value]="30">Last 30 days</option>
        <option [value]="90">Last 90 days</option>
      </select>
    </div>

    @if (loading()) {
      <div class="mt-6 flex items-center gap-2 text-sm text-slate-400">
        <span class="h-4 w-4 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"></span>
        Loading insights…
      </div>
    } @else if (error()) {
      <div class="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
        {{ error() }}
      </div>
    } @else if (data()) {
      @let d = data()!;
      <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        @for (t of tiles(d); track t.label) {
          <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-3">
            <div class="text-[11px] font-medium text-gray-500 dark:text-gray-400">{{ t.label }}</div>
            <div class="mt-1 text-xl font-semibold" [class]="t.accent">{{ t.value }}</div>
          </div>
        }
      </div>

      <div class="mt-5 mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        Click heatmaps · {{ d.sessions }} session(s)
      </div>
      @if (d.screens.length) {
        <!-- A bounded grid (page-relative height) so each card contain-fits its screen;
             the card's Full screen button gives the big view. -->
        <div class="grid h-[72vh] min-h-0 auto-rows-[minmax(0,1fr)] gap-4 xl:grid-cols-2">
          @for (s of d.screens; track s.name) {
            <app-screen-heatmap [title]="label(s)" [src]="d.sample?.domUrl ?? null"
              [atMs]="frameAt(d, s)" [points]="s.heatmap" [aspect]="aspect(d)"
              [clicks]="s.clicks" [markers]="s.topTargets" />
          }
        </div>
        <div class="mt-1 text-[10px] text-slate-400">
          Every session's clicks on that screen, over a frame from the newest recording.
        </div>
      } @else {
        <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-6 text-center text-sm text-slate-400">
          No click data in this window yet.
        </div>
      }
    }
  `,
})
export class InsightsDashboardComponent implements OnInit {
  private readonly api = inject(TrackingApiService);

  readonly days = signal(30);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly data = signal<InsightsAggregate | null>(null);

  ngOnInit(): void {
    void this.load();
  }

  onDays(value: number | string): void {
    this.days.set(Number(value) || 30);
    void this.load();
  }

  label(s: ScreenAggregate): string {
    return screenLabel(s.name);
  }

  /** Where to pause the sample recording so THIS screen is showing. */
  frameAt(d: InsightsAggregate, s: ScreenAggregate): number {
    return d.sample?.screenAtMs[s.name] ?? 0;
  }

  /** Box aspect = the sample recording's viewport, so its frame and the overlay align. */
  aspect(d: InsightsAggregate): number {
    const vp = d.sample?.viewport;
    return vp && vp.w > 0 && vp.h > 0 ? vp.w / vp.h : 16 / 9;
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      this.data.set(await this.api.getInsights(this.days()));
    } catch {
      this.error.set('Failed to load insights.');
    } finally {
      this.loading.set(false);
    }
  }

  tiles(d: InsightsAggregate): { label: string; value: string | number; accent: string }[] {
    return [
      { label: 'Sessions', value: d.sessions, accent: 'text-sky-600 dark:text-sky-400' },
      { label: 'Clicks', value: d.clickCount, accent: 'text-slate-800 dark:text-gray-100' },
      { label: 'Rage clicks', value: d.rageClicks,
        accent: d.rageClicks > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-gray-100' },
      { label: 'Dead clicks', value: d.deadClicks,
        accent: d.deadClicks > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-gray-100' },
      { label: 'Avg max scroll', value: `${d.avgMaxScrollPx}px`, accent: 'text-slate-800 dark:text-gray-100' },
    ];
  }
}
